import APILibrary, { ClassExtender, GConstructor, GenerateResource } from './APILibrary';
import { EmployerProfileResource } from 'util/resources/EmployerProfile';
import { Base } from 'active-resource';

/**
 * Supporting interfaces
 */
export enum TemplateEventFormats {
  ONLINE = 'online',
  PHONE = 'phone',
  PHYSICAL = 'physical',
}

export const DEFAULT_TEMPLATE_EVENT_FORMAT: {
  id: TemplateEventFormats;
  name: string;
  copy: string;
} = {
  id: TemplateEventFormats.ONLINE,
  name: 'online',
  copy: 'Video Call',
};

export const TEMPLATE_EVENT_FORMATS = Object.freeze([
  DEFAULT_TEMPLATE_EVENT_FORMAT,
  {
    id: TemplateEventFormats.PHONE,
    name: 'phone',
    copy: 'Phone Call',
  },
  {
    id: TemplateEventFormats.PHYSICAL,
    name: 'physical',
    copy: 'In-Person',
  },
]);

export const TemplateEventFormatMap = new Map<
  TemplateEventFormats,
  {
    id: number;
    copy: string;
  }
>([
  [TemplateEventFormats.PHONE, { id: 0, copy: 'Phone Call' }],
  [TemplateEventFormats.ONLINE, { id: 1, copy: 'Video Call' }],
  [TemplateEventFormats.PHYSICAL, { id: 2, copy: 'In-Person' }],
]);

/**
 * Relationship classes
 */
export class InterviewEventTypeAttributes {
  duration: number = null;
  employerProfileId: string = null;
  id: string = null;
  interviewType: string = null;
  name: string = null;
  slug: string = null;
  inviteUrl: string = null;
  instructions: {
    additionalInstructions?: string;
    address?: string;
    caller?: string;
    meetingUrl?: string;
    phoneNumber?: string;
  } = null;
}

export class InterviewEventTypeBelongsTo {
  employerProfile: () => EmployerProfileResource = () => new EmployerProfileResource();
}

export class InterviewEventTypeHasOne {}
export class InterviewEventTypeHasMany {}

/**
 * Utility interfaces
 */
export interface InterviewEventTypeMethods {
  getInterviewTypeLabel(): string;
  getInterviewMainInstruction(): string;
  getInstructionsCopy(): {
    primary?: string;
    secondary?: string;
  };
}

export interface InterviewEventTypeInterface
  extends InterviewEventTypeAttributes,
    InterviewEventTypeBelongsTo,
    InterviewEventTypeHasOne,
    InterviewEventTypeHasMany,
    InterviewEventTypeType {}

type InterviewEventTypeType = GConstructor<InterviewEventTypeInterface>;

export interface InterviewEventTypeResource
  extends Base<InterviewEventTypeAttributes>,
    InterviewEventTypeInterface,
    InterviewEventTypeMethods {}

export interface InterviewEventTypeResource
  extends InterviewEventTypeInterface,
    InterviewEventTypeMethods {}

/**
 * Resource methods
 */
const BaseClass = () =>
  GenerateResource<InterviewEventTypeAttributes>({
    queryName: 'interview_event_types',
    className: 'InterviewEventType',
    AttributesClass: InterviewEventTypeAttributes,
    BelongsToClasses: [
      {
        Klass: InterviewEventTypeBelongsTo,
      },
    ],
    HasOneClasses: [
      {
        Klass: InterviewEventTypeHasOne,
      },
    ],
    HasManyClasses: [
      {
        Klass: InterviewEventTypeHasMany,
      },
    ],
  });

export const InterviewEventTypeResourceClass = <TBase extends InterviewEventTypeType>(
  BaseConstructor: TBase,
): TBase =>
  class extends BaseConstructor implements InterviewEventTypeMethods {
    getInterviewTypeLabel() {
      const formatCopy = TemplateEventFormatMap.get(
        this.interviewType.toLowerCase() as TemplateEventFormats,
      )?.copy;

      if (!!formatCopy) {
        return formatCopy;
      }

      // should never happen
      return 'Event';
    }

    getInterviewMainInstruction() {
      switch (this.interviewType.toLowerCase()) {
        case 'phone':
          return this.instructions?.phoneNumber;
        case 'online':
          return this.instructions?.meetingUrl;
        case 'physical':
          return this.instructions?.address;
        default:
          return '';
      }
    }

    getInstructionsCopy() {
      if (!this.instructions) {
        return {};
      }

      switch (this.interviewType.toLowerCase()) {
        case 'phone':
          if (this.instructions?.caller === 'user_profile') {
            return {
              primary: 'My invitee will call me',
              secondary: `(You provided the phone number ${this.instructions?.phoneNumber} to your invitee)`,
            };
          }

          return {
            primary: 'I will call my invitee',
            secondary: '(You will be responsible for calling your invitee)',
          };
        case 'online':
          return {
            primary: this.instructions?.meetingUrl,
          };
        case 'physical':
          return {
            primary: this.instructions?.address,
          };
        default:
          return {};
      }
    }
  };

export class InterviewEventTypeResource extends ClassExtender<InterviewEventTypeInterface>(
  BaseClass,
  InterviewEventTypeResourceClass,
) {}

/**
 * Resource instance
 */
export const InterviewEventType = APILibrary.createResource<InterviewEventTypeResource>(
  InterviewEventTypeResource,
);
